import { create, type StoreApi, type UseBoundStore } from "zustand";

/** The column context a column-operation overlay needs at open-time. */
export interface ColumnDialogOpen {
  tableId: string;
  tableName: string;
  columnId: string;
  columnName: string | undefined;
}

export interface OpenStore<T> {
  open: T | null;
  show: (params: T) => void;
  close: () => void;
}

export type OpenStoreHook<T> = UseBoundStore<StoreApi<OpenStore<T>>>;

const openStores = new Set<{ getState: () => { close: () => void } }>();

/**
 * Open/close state for an overlay mounted once and opened from anywhere. The
 * overlay owns its working state; the store carries only what it opens on.
 *
 * A factory rather than one shared store: two overlays sharing a single `open`
 * would close each other.
 */
export const createOpenStore = <T>(): OpenStoreHook<T> => {
  const store = create<OpenStore<T>>((set) => ({
    open: null,
    show: (open) => set({ open }),
    close: () => set({ open: null }),
  }));
  openStores.add(store);
  return store;
};

/** For leaving a model: what an overlay opens on names one of its tables. */
export const closeOpenStores = (): void => {
  for (const store of openStores) store.getState().close();
};
